import React, { useEffect, useRef } from 'react';

import config from '@/config';

import { IInterstitialProps } from './types';

const testId = 'of-interstitial';

/**
 * A full-viewport surface that blocks the application until a required action
 * is completed.
 *
 * Deliberately not a Dialog. A Dialog mounts the page underneath, so that
 * page's queries still run and its errors still surface behind an opaque
 * layer. An interstitial has nothing behind it.
 *
 * It is also non-dismissible by construction rather than by prop: there is no
 * close control, no backdrop and no `onClose` for a caller to wire up. The
 * only way out is whatever the caller renders in `children`. Route-level
 * middleware is what stops the browser back button, not this component.
 *
 * Render it as a route element without a Layout wrapper. Wrapping it in Layout
 * reintroduces the nav header, which is an exit.
 */
const Interstitial = ({
  children,
  footer,
  documentTitle,
}: IInterstitialProps): React.JSX.Element => {
  const headingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!documentTitle) return undefined;
    const previous = document.title;
    document.title = documentTitle;
    return () => {
      document.title = previous;
    };
  }, [documentTitle]);

  // Nothing preceded this surface in the tab order, so send focus to the top of
  // the content rather than leaving it on <body>.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      className="flex min-h-screen flex-col bg-surface-base text-on-surface"
      data-testid={testId}
    >
      <div className="h-4 shrink-0 bg-brand-primary" />

      {/* config.logo is white-label aware (/assets/logo/<client>.png), so a
          Chemonics or FHI360 tenant gets their own mark. Deliberately not
          reusing componentsv2/NavBar/LogoImg: that wraps the image in a Link to
          the dashboard, and a navigation affordance does not belong on a
          surface with no way out. The dark treatment matches LogoImg's. */}
      <div className="flex shrink-0 items-center gap-8 border-b border-surface-border px-32 py-16">
        <img
          src={config.logo}
          alt={config.productName}
          className="h-24 w-auto dark:brightness-0 dark:invert"
        />
      </div>

      <div className="flex flex-1 justify-center px-32 py-48 max-sm:px-20 max-sm:py-32">
        <div className="w-full max-w-[620px]">
          <div ref={headingRef} tabIndex={-1} className="outline-none">
            {children}
          </div>

          {footer ? (
            <div className="mt-24 border-t border-surface-border pt-20 text-12 leading-relaxed text-on-surface-muted">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export { testId as InterstitialTestId };
export type { IInterstitialProps };
export default Interstitial;
